/* eslint-disable no-array-constructor */
import { h, Component } from 'preact';
import { getRatingsBySecurity } from '@os/cloudquote';
import { Text } from 'preact-i18n';
import { Pill, FinanceSpinner } from '../glyphs';
import { getAverage } from '../../lib/util';
import { LINE_HEIGHT, OFFSET_TOP, Y_WINDOW_MAX_RANGE, BARS_CONTAINER_WIDTH } from './constants';
import cx from 'classnames';
import style from './style';

const INTL_NS = 'analysts-panel.priceTargetsChart';

function getExtents(bars) {
	return [
		0,
		bars.reduce((acc, value) => Math.max(acc, value), 0) * Y_WINDOW_MAX_RANGE
	];
}

function sortAndNormalizeData({ columns, rows }) {
	return rows
		.sort((a, b) => (
			new Date(b[columns.indexOf('ratingDate')]).getTime() - new Date(a[columns.indexOf('ratingDate')]).getTime()
		))
		.slice(0, 5)
		.map((row) => (row[columns.indexOf('priceTarget')]));
}

export default class PriceTargetsChart extends Component {
	componentWillMount() {
		getRatingsBySecurity(this.props.symbol)
			.then(sortAndNormalizeData)
			.then((bars) => this.setState({ bars }));
	}

	render({ width }, { bars }) {
		let height = 250;
		let halfWidth = width/2;
		if (!bars) {
			return <FinanceSpinner />;
		}

		return (
			<div class={style.priceTargets}>
				<div class={style.header}>
					<Text id={`${INTL_NS}.priceTargets`} />
				</div>
				<BarChart
					bars={bars}
					width={halfWidth}
					height={height}
				/>
			</div>
		);
	}
}

/**
 * Calculate the percentage of `value` within the range of the given extents and return a proportional height for that value.
 * @param {Number} height       the total height of the container
 * @param {Number} value        a value from data. This value is expected to be within the provided extents
 * @param {Number[]} extents    the extents of the data forming a range.
 * @returns {Number}            a height that is proportional to the total height based on `value`.
 * @example <caption>Calculate the height of data point "20" within height=100 and extents=[0, 100]</caption>
 * let height = 100
 * let value = 20;
 * let extents = [0, 100];
 * let scaledHeight = scaleY(height, value, extents);
 * console.log(scaledHeight); // 20
 */
function scaleY(height, value, extents = [0, 100]) {
	let [ min, max ] = extents;
	let range = max - min;
	let percent = 1 - ((range - value) / range);
	let scaled = height * percent;
	return scaled;
}

const NO_PADDING = { padding: 0 }; // force no padding  on this svg element. padding will mess up svg calculations.

const BarChart = ({ width, height, bars = [], ...props }) => {
	if (!height || !width) { return; }
	let barsContainerWidth = width * BARS_CONTAINER_WIDTH;
	let padding = barsContainerWidth * .05;
	let containerHeight = height - 3 * LINE_HEIGHT - OFFSET_TOP;
	let barsContainerTransformX = (parseFloat(1 - BARS_CONTAINER_WIDTH) * 100).toFixed();
	let averageEstimate = getAverage(bars);

	return (
		<svg style={NO_PADDING} class={cx(style.barChart, props.class)} width={width} height={height}>
			<YAxis
				bars={bars}
				containerHeight={containerHeight}
				containerWidth={width}
			/>
			<svg x={`${barsContainerTransformX}%`}>
				{bars.length ? (
					<VerticalBarGroup
						bars={bars}
						padding={padding}
						containerHeight={containerHeight}
						containerWidth={barsContainerWidth}
					/>
				) : (
					<text class={style.noData} x={barsContainerWidth / 2} y={containerHeight / 2} text-anchor="middle">
						<Text id={`${INTL_NS}.dataUnavailable`} />
					</text>
				)}
				<XAxisLabel
					containerWidth={barsContainerWidth}
					containerHeight={height}
				>
					<Text id={`${INTL_NS}.analystsPriceTargets`} />
				</XAxisLabel>
				<ReferenceLine
					containerHeight={containerHeight}
					containerWidth={barsContainerWidth}
					bars={bars}
					value={averageEstimate}
				/>
			</svg>

		</svg>
	);
};

const XAxisLabel = ({ children, containerHeight, containerWidth }) => (
	<text text-anchor="middle" x={containerWidth / 2} y={containerHeight - LINE_HEIGHT / 4}>
		{children}
	</text>
);

const ReferenceLine = ({ containerHeight, containerWidth, bars, value }) => {
	let extents = getExtents(bars);
	let lineY = containerHeight - scaleY(containerHeight, value, extents) + LINE_HEIGHT / 2;
	if (!value) return null;

	return (
		<g class={style.dashedLine}>
			<line
				y1={lineY}
				y2={lineY}
				x1={0}
				x2={containerWidth}
			/>
			<Pill
				centerY
				y={lineY}
				x={0}
				label={value || <Text id={`${INTL_NS}.noData`} />}
			/>
		</g>
	);
};

const VerticalBarGroup = ({ bars, padding, containerHeight, containerWidth }) => {
	let numBars = bars.length;
	let extents = getExtents(bars);

	return (
		<g class={style.barGroup}>
			{bars.map((value, index) => (
				<VerticalBar
					containerHeight={containerHeight}
					barHeight={scaleY(containerHeight, value, extents)}
					barWidth={containerWidth / numBars}
					padding={padding}
					index={index}
					value={value}
				/>
			))}
		</g>
	);
};

// An svg group of lines all equal distance apart
const YAxis = ({ bars, containerHeight, containerWidth, numLines = 11 }) => {
	let max = getExtents(bars)[1];
	return (
		<g>
			{Array(...Array(numLines)).map((_, index, arr) => {

				let lineY = scaleY(containerHeight, index, [ 0, arr.length - 1 ]) + OFFSET_TOP;
				let axisLabelContainerWidth = containerWidth * (1 - BARS_CONTAINER_WIDTH);
				let rightAlignTextX = axisLabelContainerWidth - 5;
				let customLabelTextX = axisLabelContainerWidth * .7;
				let textY = lineY + LINE_HEIGHT / 2;
				return (
					<g>
						<line
							x1={containerWidth * (1 - BARS_CONTAINER_WIDTH)}
							x2={containerWidth}
							y1={lineY}
							y2={lineY}
						/>
						{index % 2 === 0 && index < arr.length - 1 && (
							<text
								text-anchor={'end'}
								x={rightAlignTextX}
								y={textY}
							>
								{scaleY(max, arr.length - index, [0, arr.length]).toFixed(1)}
							</text>
						)}
						{index === arr.length - 1 && (
							<text
								text-anchor={'middle'}
								x={customLabelTextX}
								y={containerHeight + LINE_HEIGHT / 2}
							>
								<tspan x={customLabelTextX} dy={0}><Text id={`${INTL_NS}.US`}>US</Text></tspan>
								<tspan x={customLabelTextX} dy={LINE_HEIGHT * 1.23}>
									<Text id={`${INTL_NS}.currency`}>Dollars</Text>
								</tspan>
							</text>
						)}
					</g>
				);
			})}
		</g>
	);
};

const VerticalBar = ({ padding = 0, containerHeight, barHeight, barWidth, index, value }) => {
	let barX = barWidth * index + padding;
	let barY = containerHeight - barHeight + OFFSET_TOP;
	barWidth = barWidth - (2 * padding);

	let textY = containerHeight + 1.25 * LINE_HEIGHT + OFFSET_TOP;

	return (
		<g>
			<rect
				class={style.verticalBar}
				height={barHeight}
				width={barWidth}
				x={barX}
				y={barY}
			/>
			<text x={barX + barWidth / 2} y={textY} text-anchor={'middle'}>{value}</text>
		</g>
	);
};
